import { Language } from "../../i18n/languages";
import { hasShares, ShareTypeFilter } from "../../stores/ShareholdersStore";
import {
  ExtendedShareholderDTO,
  isOwnShareholder,
  totalNumberOfShares,
  totalNumberOfSharesOfShareholder,
  totalNumberOfVotes,
  totalNumberOfVotesForShareholder,
} from "../../types/Shareholder";
import { ShareType, ShareTypeCode, shareTypeLabel } from "../../common/share";
import { formatBirthDateOrOrganisationNumber } from "../shareholderRegisterDomain";
import { formatNumber, formatPercent, percentOf } from "../../utils/numbers";
import { TFunction, TMessage } from "../../i18n/tt";
import { ReactNode } from "react";
import { InfoButton } from "../../components-v2/InfoButton";
import { Icon } from "../../components-v2/Icon";
import { VBox } from "../../components-v2/Box";
import { HoverTooltip } from "../../components-v2/Tooltip";

export class EmptyShareholderStateEvents {
  onImportShareholders: () => void;
  onImportFromFile: () => void;
  onAddShareholdersManually: () => void;
  onAddShareholdersFromPortfolio: () => void;

  constructor(
    onImportShareholders: () => void,
    onImportFromFile: () => void,
    onAddShareholdersManually: () => void,
    onAddShareholdersFromPortfolio: () => void,
  ) {
    this.onImportShareholders = onImportShareholders;
    this.onImportFromFile = onImportFromFile;
    this.onAddShareholdersManually = onAddShareholdersManually;
    this.onAddShareholdersFromPortfolio = onAddShareholdersFromPortfolio;
  }
}

export function prepTableColumns(
  locale: Language,
  shareTypeFilter: ShareTypeFilter,
  sortedShareholders: ExtendedShareholderDTO[],
  shareSumBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  calculatedVotesBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  filteredTypes: ShareType[],
  t: TFunction,
): ShareholderColumn[] {
  const sharesColumns =
    shareTypeFilter === "overview"
      ? prepOverviewColumns(locale, shareSumBySharetypeCode, calculatedVotesBySharetypeCode)
      : shareTypeFilter === "votes"
        ? prepVotesColumns(locale, filteredTypes, calculatedVotesBySharetypeCode)
        : prepSharesColumns(locale, filteredTypes, shareSumBySharetypeCode);

  return sortedShareholders.length > 0
    ? [
        {
          shareTypeName: "icon",
          label: { translated: "" },
          align: "left",
          width: 10,
          getValue: (s: ExtendedShareholderDTO) => (
            <HoverTooltip
              content={t(
                hasShares(s)
                  ? s.isCompany
                    ? "shareholders.company-shareholder"
                    : "shareholders.personal-shareholder"
                  : "shareholders.no-shares",
              )}
            >
              {({ targetRef }) => (
                <VBox ref={targetRef}>
                  <Icon
                    name={s.isCompany ? "buildings" : "user-square"}
                    className={hasShares(s) ? undefined : "tw-opacity-20"}
                  />
                </VBox>
              )}
            </HoverTooltip>
          ),
        },
        {
          name: "name",
          label: { key: "shareholders.name" },
          align: "left",
          getValue: (s) => s.name,
        },
        {
          name: "birthDateOrOrganisationNumber",
          label: { key: "shareholders.birthDateOrOrganisationNumber" },
          align: "left",
          getValue: (s) => formatBirthDateOrOrganisationNumber(locale, s),
        },
        ...sharesColumns,
      ]
    : [];
}

function prepVotesColumns(
  lang: Language,
  filteredTypes: ShareType[],
  calculatedVotesBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
): ShareholderColumn[] {
  const totalVotesInCompany = totalNumberOfVotes(calculatedVotesBySharetypeCode);
  return [
    {
      shareTypeName: "totalVotes",
      label: { key: "shareholders.total-votes" },
      align: "right",
      width: 60,
      getValue: (s: ExtendedShareholderDTO) =>
        isOwnShareholder(s) ? (
          <InfoButton infoText={{ key: "transactions.own-shares-info" }} />
        ) : (
          formatNumber(lang, totalNumberOfVotesForShareholder(s))
        ),
    },
    {
      shareTypeName: "percentageVotes",
      label: { key: "shareholders.total-votes-percent" },
      align: "right",
      width: 60,
      getValue: (s: ExtendedShareholderDTO) =>
        formatPercent(lang, percentOf(totalNumberOfVotesForShareholder(s), totalVotesInCompany)),
    },
    ...filteredTypes.flatMap<ShareholderColumn>(({ sharetypeCode }) => [
      {
        shareTypeName: sharetypeCode,
        label: {
          key: "shareholders.votes",
          params: {
            shareTypeName: shareTypeLabel(sharetypeCode),
          },
        },
        align: "right",
        width: 60,
        getValue: (s) =>
          isOwnShareholder(s) ? null : formatNumber(lang, s.calculatedVotes[sharetypeCode]),
      },
      {
        shareTypeName: `${sharetypeCode}-percentage`,
        label: {
          key: "shareholders.percent-votes",
          params: {
            shareTypeName: shareTypeLabel(sharetypeCode),
          },
        },
        align: "right",
        width: 60,
        getValue: (s) =>
          isOwnShareholder(s)
            ? null
            : formatPercent(
                lang,
                s.calculatedVotes[sharetypeCode]! /
                  calculatedVotesBySharetypeCode[sharetypeCode]! || 0,
              ),
      },
    ]),
  ];
}

function prepOverviewColumns(
  lang: Language,
  shareSumBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  calculatedVotesBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
): ShareholderColumn[] {
  const totalSharesInCompany = totalNumberOfShares(shareSumBySharetypeCode);
  const totalVotesInCompany = totalNumberOfVotes(calculatedVotesBySharetypeCode);
  return [
    {
      shareTypeName: "totalshares",
      label: { key: "transactions.number-of-shares" },
      align: "right",
      width: 60,
      getValue: (s: ExtendedShareholderDTO) =>
        formatNumber(lang, totalNumberOfSharesOfShareholder(s)),
    },
    {
      shareTypeName: "percentageShares",
      label: { key: "ownership.table.ownership" },
      align: "right",
      width: 60,
      getValue: (s) =>
        formatPercent(
          lang,
          percentOf(totalNumberOfSharesOfShareholder(s), totalSharesInCompany) || 0,
        ),
    },
    {
      shareTypeName: "totalVotes",
      label: { key: "transactions.number-of-votes" },
      align: "right",
      width: 60,
      getValue: (s: ExtendedShareholderDTO) =>
        isOwnShareholder(s) ? (
          <InfoButton infoText={{ key: "transactions.own-shares-info" }} />
        ) : (
          formatNumber(lang, totalNumberOfVotesForShareholder(s))
        ),
    },
    {
      shareTypeName: "percentageVotes",
      label: { key: "transactions.percentage-of-votes" },
      align: "right",
      width: 60,
      getValue: (s) =>
        formatPercent(lang, percentOf(totalNumberOfVotesForShareholder(s), totalVotesInCompany)),
    },
  ];
}

function prepSharesColumns(
  lang: Language,
  filteredTypes: ShareType[],
  shareSumBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
) {
  return filteredTypes.flatMap<ShareholderColumn>(({ sharetypeCode }) => [
    {
      shareTypeName: sharetypeCode,
      label: {
        key: "shareholders.shares",
        params: { shareTypeName: shareTypeLabel(sharetypeCode) },
      },
      align: "right",
      width: 60,
      getValue: (s) => formatNumber(lang, s.calculatedShares[sharetypeCode]),
    },
    {
      shareTypeName: `${sharetypeCode}-percentage`,
      label: {
        key: "shareholders.percent-shares",
        params: {
          shareTypeName: shareTypeLabel(sharetypeCode),
        },
      },
      align: "right",
      width: 60,
      getValue: (s) =>
        formatPercent(
          lang,
          s.calculatedShares[sharetypeCode]! / shareSumBySharetypeCode[sharetypeCode]! || 0,
        ),
    },
  ]);
}

interface ShareholderColumn {
  name?: keyof ExtendedShareholderDTO;
  shareTypeName?: string;
  label: TMessage;
  align?: "left" | "right";
  getValue: (s: ExtendedShareholderDTO) => ReactNode;
  notSorted?: boolean;
  width?: number;
}
